import { useTranslation } from "react-i18next";
import { Skeleton } from "@mantine/core";
import FilterCheckboxList from "@/components/molecules/FilterCheckboxList";
import FilterPanel from "@/components/molecules/FilterPanel";
import FilterSection from "@/components/molecules/FilterSection";
import OrderStatusEnum from "@/constants/enums/OrderStatusEnum";
import OrderTypeEnum from "@/constants/enums/OrderTypeEnum";
import { useAuth } from "@/contexts/AuthContext";
import { useGetAllSalesItemsByLanguageId } from "@/services/Product";
import { useOrderStore } from "@/stores/ordersStore";
import { effectiveOrderTypes, SELECTABLE_ORDER_TYPES } from "../const/orderTypes";
import { useOrderStatusOptions } from "./useOrderStatusOptions";

interface OrdersFilterProps {
  /** Cap the panel height and scroll inside it. Off in the mobile drawer, which scrolls itself. */
  scrollable?: boolean;
  /** Placement (width, flex) is up to the page */
  className?: string;
}

const toggleValue = <T,>(values: T[], value: T): T[] =>
  values.includes(value) ? values.filter((v) => v !== value) : [...values, value];

/**
 * The orders filter: status, order type, product type and quantity check as collapsible checkbox sections.
 * The state itself is kept in sync with the URL by useOrdersFilterSync, which the page mounts.
 */
export default function OrdersFilter({ scrollable = true, className }: OrdersFilterProps) {
  const orderTypes = useOrderStore((state) => state.orderTypes);
  const setOrderTypes = useOrderStore((state) => state.setOrderTypes);
  const statuses = useOrderStore((state) => state.statuses);
  const setStatuses = useOrderStore((state) => state.setStatuses);
  const salesItemId = useOrderStore((state) => state.salesItemId);
  const setSalesItemId = useOrderStore((state) => state.setSalesItemId);
  const onlyConfirmedDespiteWarning = useOrderStore((state) => state.onlyConfirmedDespiteWarning);
  const setOnlyConfirmedDespiteWarning = useOrderStore((state) => state.setOnlyConfirmedDespiteWarning);

  const { t } = useTranslation();
  const { user } = useAuth();
  const statusOptions = useOrderStatusOptions();
  const { data: salesItems, isLoading: isLoadingSalesItems } = useGetAllSalesItemsByLanguageId(
    user?.language?.id as number,
    { enabled: !!user?.language?.id }
  );

  const hasActiveFilters =
    statuses.length > 0 ||
    effectiveOrderTypes(orderTypes) !== null ||
    salesItemId !== null ||
    onlyConfirmedDespiteWarning;

  const resetFilters = () => {
    setStatuses([]);
    setOrderTypes(null);
    setSalesItemId(null);
    setOnlyConfirmedDespiteWarning(false);
  };

  const toggleOrderType = (type: OrderTypeEnum) => {
    const next = toggleValue(orderTypes ?? [], type);
    // Nothing ticked shows every order, same as everything ticked
    setOrderTypes(next.length > 0 ? next : null);
  };

  return (
    <FilterPanel
      onReset={resetFilters}
      resetDisabled={!hasActiveFilters}
      defaultOpen={["status", "orderType", "productType", "quantityCheck"]}
      scrollable={scrollable}
      className={className}
      data-testid="orders-filter"
      resetTestId="orders-filter-reset"
    >
      <FilterSection value="status" title={t("orders:orderStatus")}>
        <FilterCheckboxList
          ariaLabel={t("orders:orderStatus")}
          options={statusOptions}
          selected={statuses}
          onToggle={(value) => setStatuses(toggleValue(statuses, value as OrderStatusEnum))}
          data-testid="order-status-checkboxes"
        />
      </FilterSection>

      <FilterSection value="orderType" title={t("orders:orderType")}>
        <FilterCheckboxList
          ariaLabel={t("orders:orderType")}
          options={SELECTABLE_ORDER_TYPES.map((type) => ({
            value: String(type),
            label: t(type === OrderTypeEnum.BUY ? "actions:buy" : "actions:sell"),
            testId: `orders-type-${type === OrderTypeEnum.BUY ? "buy" : "sell"}`,
          }))}
          selected={(orderTypes ?? []).map(String)}
          onToggle={(value) => toggleOrderType(Number(value))}
          data-testid="order-type-checkboxes"
        />
      </FilterSection>

      <FilterSection value="productType" title={t("product:productType")}>
        {isLoadingSalesItems ? (
          <Skeleton height={120} />
        ) : (
          // One product type at a time (the orders endpoint takes a single salesItemId); ticking the
          // selected one again clears it.
          <FilterCheckboxList
            ariaLabel={t("product:productType")}
            options={(salesItems ?? []).map((item) => ({
              value: String(item.salesItemId),
              label: item.salesItemTranslated,
            }))}
            selected={salesItemId === null ? [] : [String(salesItemId)]}
            onToggle={(value) => setSalesItemId(salesItemId === Number(value) ? null : Number(value))}
            data-testid="order-product-type-checkboxes"
          />
        )}
      </FilterSection>

      <FilterSection value="quantityCheck" title={t("orders:quantityCheck.title")}>
        {/* One option: ticked lists only the flagged orders, unticked lists every order. */}
        <FilterCheckboxList
          ariaLabel={t("orders:quantityCheck.title")}
          options={[
            {
              value: "confirmedDespiteWarning",
              label: t("orders:quantityCheck.confirmedDespiteWarning"),
              testId: "orders-quantity-check-confirmed",
            },
          ]}
          selected={onlyConfirmedDespiteWarning ? ["confirmedDespiteWarning"] : []}
          onToggle={() => setOnlyConfirmedDespiteWarning(!onlyConfirmedDespiteWarning)}
          data-testid="order-quantity-check-checkboxes"
        />
      </FilterSection>
    </FilterPanel>
  );
}
